import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { usePortfolio } from '@/context/PortfolioContext';
import { Save, MessageCircle, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { chatbotResponses as defaultChatbotResponses } from '@/data/mock';

const EMPTY_RESPONSES = {
  greeting: '',
  projects: '',
  skills: '',
  experience: '',
  certifications: '',
  education: '',
  contact: '',
  default: '',
};

const AdmChatbot = () => {
  const { portfolioData, updateChatbotResponses } = usePortfolio();
  const [responses, setResponses] = useState(EMPTY_RESPONSES);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setResponses({
      ...EMPTY_RESPONSES,
      ...defaultChatbotResponses,
      ...(portfolioData?.chatbotResponses || {}),
    });
  }, [portfolioData]);

  const handleChange = (field, value) => {
    setResponses((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const result = await updateChatbotResponses(responses);
      if (result?.success) {
        toast.success('Réponses du chatbot enregistrées');
      } else {
        toast.error(result?.message || 'Erreur lors de la sauvegarde des réponses du chatbot');
      }
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde des réponses du chatbot');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setResponses({
      ...EMPTY_RESPONSES,
      ...defaultChatbotResponses,
      ...(portfolioData?.chatbotResponses || {}),
    });
    toast.success('Formulaire réinitialisé');
  };

  const responseFields = [
    { key: 'greeting', label: 'Message de bienvenue', icon: <MessageCircle className="w-4 h-4 mr-1" /> },
    { key: 'projects', label: 'Réponse sur les projets', icon: <Sparkles className="w-4 h-4 mr-1" /> },
    { key: 'skills', label: 'Réponse sur les compétences', icon: <Sparkles className="w-4 h-4 mr-1" /> },
    { key: 'experience', label: "Réponse sur l'expérience", icon: <Sparkles className="w-4 h-4 mr-1" /> },
    { key: 'certifications', label: 'Réponse sur les certifications', icon: <Sparkles className="w-4 h-4 mr-1" /> },
    { key: 'education', label: "Réponse sur l'éducation", icon: <Sparkles className="w-4 h-4 mr-1" /> },
    { key: 'contact', label: 'Réponse sur le contact', icon: <MessageCircle className="w-4 h-4 mr-1" /> },
    { key: 'default', label: 'Réponse par défaut', icon: <MessageCircle className="w-4 h-4 mr-1" /> },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
          <MessageCircle className="w-6 h-6 mr-2" />
          Gestion du Chatbot
        </h2>
        <div className="space-x-2">
          <Button variant="outline" onClick={handleReset}>
            Réinitialiser
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            <Save className="w-4 h-4 mr-2" />
            {isSaving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {responseFields.map((field) => (
          <Card key={field.key}>
            <CardHeader>
              <CardTitle className="flex items-center">
                {field.icon}
                {field.label}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div>
                <Label htmlFor={`response-${field.key}`} className="flex items-center mb-1">
                  Réponse personnalisée
                </Label>
                <Textarea
                  id={`response-${field.key}`}
                  value={responses[field.key] || ''}
                  onChange={(e) => handleChange(field.key, e.target.value)}
                  placeholder={`Entrez la réponse pour ${field.label.toLowerCase()}`}
                  rows={4}
                />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Aperçu des réponses</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {responseFields.map((field) => (
              <div key={field.key} className="border-l-4 border-emerald-500 pl-4">
                <h4 className="font-medium text-gray-900 dark:text-white">{field.label}</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {responses[field.key]?.trim() || 'Non défini'}
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdmChatbot;
